// 读取当前登录用户 user_id 的「纯函数」单一事实源 + 预取用户隔离 header 常量 re-export。
//
// 用途：SW 预取去重需要「用户维度」隔离，避免「A 登录预热的响应被 8s 内登出/换号的请求复用」。
// 真实鉴权靠 cookie，但 cookie（httpOnly）SW 读不到、也不含明文 user_id；而 localStorage 的
// '/user' 里存了 user 对象（含 user_id），登录写入、登出清除（lib/auth/auth.ts signOut）。
// 故用它做隔离维度：boot 预热侧与 axios 拦截侧都用 readUserId() 取值，写进 USER_ID_HEADER；SW
// 从请求 header 读出拼进 dedupKey（见 prefetch-sw.ts）。该 header 纯前端自用，SW 转发后端前会剥掉，
// 永不送达后端 → 无需后端 CORS 白名单（详见 constant/prefetch-headers.ts）。
//
// 缺失（登出态）返回空串：登出态之间互相去重（都空），与任何登录态隔离。
//
// storage key 直接复用 constant/storage.ts 的 storageKey.USER（单一事实源，无漂移）。
// storage.ts 是零 import 的纯 enum，esbuild 打包 boot 时会把 storageKey.USER 内联成字面量
// '/user'、tree-shake 掉其余成员，故 boot 产物不变胖（已验证）。

import { storageKey } from './storage'

// 预取用户隔离 header 名。app/boot/SW 三侧共用，统一从中立的 constant/prefetch-headers.ts 取
// （单一事实源，零漂移）。此处 re-export 是为兼容现有 `@/constant/prefetch-user` 引用方
// （utils/axios、framework 都连带 readUserId 一起 import），无需改它们的 import 路径。
export { USER_ID_HEADER } from './prefetch-headers'

/** 读当前登录 user_id；登出态/异常返回空串。 */
export const readUserId = (): string => {
  try {
    const raw = localStorage?.getItem?.(storageKey.USER)
    if (!raw) return ''
    const user = JSON.parse(raw) as { user_id?: string | number } | null
    return user?.user_id != null ? String(user.user_id) : ''
  } catch {
    return ''
  }
}

/**
 * 读当前登录用户的 nsfw 开关（后端权威值）；登出态/异常返回 false。
 *
 * 与页面列表请求的 nsfw 同源：页面 nsfw = homeNSFWState atom，而该 atom 的权威源是后端 user.nsfw
 * （hooks/useInit.tsx 的 setNsfw(user.nsfw) 播种，登出态回落 DEFAULT_NSFW=false）。userState atom 用
 * localStorageWriteJsonEffect 把完整 user 对象（含 nsfw）持久化到 localStorage '/user'，故 boot 从同一
 * 对象读 nsfw 即与页面同源。比读 homeNSFWState（只写不回读的过期态）准确。
 */
export const readUserNsfw = (): boolean => {
  try {
    const raw = localStorage?.getItem?.(storageKey.USER)
    if (!raw) return false
    const user = JSON.parse(raw) as { nsfw?: boolean } | null
    return user?.nsfw === true
  } catch {
    return false
  }
}
